import { Inject, Injectable, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { from, Observable, Subject, Subscription } from 'rxjs';

import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Network } from '@capacitor/network';

export enum FrontendResumeReason {
  backOnline = 'Back Online',
  tabOpened = 'Tab Opened',
  tabClosed = 'Tab Closed',
}

@Injectable({ providedIn: 'root' })
export class FrontendResumeBackgroundService implements OnDestroy {
  resume$: Observable<FrontendResumeReason>;

  private readonly resumeSource$ = new Subject<FrontendResumeReason>();
  private nativeOnline = true;
  private readonly nativeSubscription = new Subscription();
  private readonly nativeHandles: PluginListenerHandle[] = [];

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.resume$ = this.resumeSource$.asObservable();
  }

  init(): void {
    this.document.addEventListener('visibilitychange', this.visibilityChangeListener);
    window.addEventListener('online', this.onlineListener);

    if (Capacitor.isNativePlatform()) {
      this.initNativeListeners();
    }
  }

  ngOnDestroy(): void {
    this.document.removeEventListener('visibilitychange', this.visibilityChangeListener);
    window.removeEventListener('online', this.onlineListener);
    this.nativeSubscription.unsubscribe();
    this.nativeHandles.forEach((handle) => {
      handle.remove().catch(() => undefined);
    });
    this.nativeHandles.length = 0;
  }

  forceOnlineStatus(): void {
    this.onlineListener();
  }

  isOnline(): boolean {
    if (Capacitor.isNativePlatform()) {
      return this.nativeOnline;
    }

    return navigator.onLine;
  }

  private initNativeListeners(): void {
    const appListener = App.addListener('appStateChange', ({ isActive }) => {
      this.resumeSource$.next(isActive ? FrontendResumeReason.tabOpened : FrontendResumeReason.tabClosed);
    });
    this.nativeSubscription.add(from(appListener).subscribe((handle) => {
      this.nativeHandles.push(handle);
    }));

    const networkListener = Network.addListener('networkStatusChange', (networkStatus) => {
      this.nativeOnline = networkStatus.connected;
      if (networkStatus.connected) {
        this.resumeSource$.next(FrontendResumeReason.backOnline);
      }
    });
    this.nativeSubscription.add(from(networkListener).subscribe((handle) => {
      this.nativeHandles.push(handle);
    }));

    // seedd the cache so isOnline method is accurate before the first change event arrives
    this.nativeSubscription.add(from(Network.getStatus()).subscribe((networkStatus) => {
      this.nativeOnline = networkStatus.connected;
    }));
  }

  private readonly onlineListener = (): void => {
    this.resumeSource$.next(FrontendResumeReason.backOnline);
  };

  private readonly visibilityChangeListener = (): void => {
    this.resumeSource$.next(this.document.hidden ? FrontendResumeReason.tabClosed : FrontendResumeReason.tabOpened);
  };
}
